import { toast } from '../components/Toast';
import { getApiUrl } from '../config/api';

// API URL helper function (deprecated - use config/api.js instead)
export const getApiUrl_deprecated = (endpoint) => {
  // In production (when served from the same domain), use relative URLs
  // In development, the proxy in package.json will handle the routing
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
  
  // If the endpoint already starts with /api, use it as is
  if (endpoint.startsWith('/api/')) {
    return endpoint;
  }
  
  // Otherwise, prepend /api/
  return `/api/${cleanEndpoint}`;
};

// Utility function for logging out user
export const logoutUser = (setUser, clearCart, navigate, showNotification = true) => {
  // Clear user data
  setUser(null);
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  
  // Clear user-specific cart from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  if (currentUser.id) {
    localStorage.removeItem(`cart_${currentUser.id}`);
  }
  
  // Clear cart in state
  if (clearCart) {
    clearCart();
  }
  
  // Show notification if requested
  if (showNotification) {
    toast.info('You have been logged out due to inactivity.');
  }
  
  // Navigate to home page
  if (navigate) {
    navigate('/');
  }
  
  console.log('User logged out');
};

// Check if JWT token is expired
export const isTokenExpired = (token, user = null) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    
    // Check if user is admin - try multiple sources
    let isAdmin = false;
    
    // First, try the provided user parameter
    if (user && (user.role === 'admin' || user.role === 'ADMIN')) {
      isAdmin = true;
    }
    
    // If no user provided, try localStorage
    if (!isAdmin) {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      isAdmin = storedUser.role === 'admin' || storedUser.role === 'ADMIN';
    }
    
    // As a fallback, check if the JWT subject (email) indicates admin
    if (!isAdmin && payload.sub && payload.sub.includes('admin')) {
      isAdmin = true;
    }
    
    // For admin users, add a small buffer (30 seconds) to prevent premature logout
    // due to small timing differences between frontend and backend
    const bufferTime = isAdmin ? 30 : 0;
    const adjustedExpTime = payload.exp + bufferTime;
    
    const isExpired = adjustedExpTime < currentTime;
    
    console.log('=== TOKEN EXPIRY CHECK ===');
    console.log('Provided user:', user);
    console.log('Stored user role:', JSON.parse(localStorage.getItem('user') || '{}').role);
    console.log('JWT subject:', payload.sub);
    console.log('Is admin:', isAdmin);
    console.log('Token payload exp:', payload.exp);
    console.log('Buffer time (seconds):', bufferTime);
    console.log('Adjusted exp time:', adjustedExpTime);
    console.log('Current time:', currentTime);
    console.log('Is expired:', isExpired);
    console.log('Time remaining (seconds):', payload.exp - currentTime);
    console.log('Time remaining with buffer (seconds):', adjustedExpTime - currentTime);
    
    return isExpired;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

// Get time until token expires (in milliseconds)
export const getTokenTimeRemaining = (token) => {
  if (!token) return 0;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    const timeRemaining = (payload.exp - currentTime) * 1000;
    
    console.log('🕐 GET TOKEN TIME REMAINING:');
    console.log('Token exp:', payload.exp, '(', new Date(payload.exp * 1000), ')');
    console.log('Current time:', currentTime, '(', new Date(), ')');
    console.log('Time remaining (ms):', timeRemaining);
    console.log('Time remaining (minutes):', Math.round(timeRemaining / 1000 / 60));
    
    return Math.max(0, timeRemaining);
  } catch (error) {
    console.error('Error getting token time remaining:', error);
    return 0;
  }
};

// Fetch wrapper with authentication
export const fetchWithAuth = async (url, options = {}) => {
  const { suppressAutoLogout = false, ...requestOptions } = options;
  const token = localStorage.getItem('token');
  
  console.log('=== FETCH WITH AUTH DEBUG ===');
  console.log('URL:', url);
  console.log('Options:', options);
  console.log('Token exists:', !!token);
  
  if (!token) {
    console.error('No authentication token found');
    throw new Error('No authentication token found');
  }
    if (isTokenExpired(token)) {
    console.error('Token is expired, logging out');
    // Token is expired, log out user
    logout();
    throw new Error('Authentication token expired');
  }
    // Use the centralized API URL configuration directly
  const apiUrl = getApiUrl(url);
  
  console.log('Final API URL:', apiUrl);
  
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...requestOptions.headers
  };
  
  console.log('Request headers:', headers);
  
  try {
    const response = await fetch(apiUrl, {
      ...requestOptions,
      headers
    });
    
    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);
    
    // If response is 401, token might be invalid
    if (response.status === 401) {
      console.error('401 Unauthorized response');
      if (!suppressAutoLogout) {
        console.error('Auto-logout enabled - logging out');
        logout();
      }
      throw new Error('Authentication failed');
    }
    
    return response;
  } catch (error) {
    console.error('Fetch with auth error:', error);
    throw error;
  }
};

// Simple logout function for admin panel
export const logout = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  window.location.href = '/';
};
